import { GenericError, UnknownError } from '@cian/peperrors';

import { isChatSyncError, ChatSyncError } from '../../errors/ChatSyncError';
import { mapChat } from '../../mappers';
import { TStatus } from '../../repositories/chats/entities/synchronization/V2ScrollSynchronizationRequestSchema';
import {
  TWebChatsSyncRequest,
  TWebChatsSyncResponse,
  fetchWebChatsSync,
} from '../../repositories/chats/v2/web/chats/sync';
import { IChat } from '../../types/chatList';
import { TContext } from '../../types/context';
import { ETelemetryEvents } from '../../types/telemetry';

export type TSyncChatsV2ParamsRequest = Omit<TWebChatsSyncRequest, 'status'> & {
  /** Раздел чатов **/
  status?: TStatus;
};

type TSyncChatsV2Params = {
  parameters: TSyncChatsV2ParamsRequest;
  cookies?: string;
};

const DOMAIN = 'critical.syncChats';
const DEFAULT_ERROR_MESSAGE = 'Неизвестная ошибка при синхронизации чатов';

interface ISyncChatsResult {
  chats: IChat[];
  movedChatIds: string[];
  error: string | null;
}

export async function syncChatsV2(
  { logger, httpApi, telemetry }: TContext,
  { cookies, parameters }: TSyncChatsV2Params,
): Promise<ISyncChatsResult> {
  const { status } = parameters;

  const options: TWebChatsSyncRequest = {
    ...parameters,
    status: status || 'active',
  };

  const startTime = Date.now();

  telemetry.increment(ETelemetryEvents.ChatsSynchronizeInit);

  try {
    const { response, statusCode }: TWebChatsSyncResponse = await fetchWebChatsSync({
      httpApi,
      parameters: options,
      config: {
        requestConfig: {
          headers: cookies ? [['Cookie', cookies]] : [],
          withCredentials: true,
        },
      },
    });

    if (statusCode === 200) {
      telemetry.increment(ETelemetryEvents.ChatsSynchronizeSuccess);
      telemetry.timing(ETelemetryEvents.ChatsSynchronizeTime, Date.now() - startTime);

      return {
        chats: response.chats.map(mapChat),
        movedChatIds: response.movedChatIds || [],
        error: null,
      };
    }

    const error = response.errors?.[0];

    if (statusCode === 400 && error && isChatSyncError(error)) {
      const errorToLog = new ChatSyncError({
        domain: DOMAIN,
        message: error.message,
      });

      logger.error(errorToLog, {
        microserviceName: 'chats',
        pathApi: '/v2/web/chats/sync/',
        parameters: JSON.stringify(options),
      });

      return {
        chats: [],
        movedChatIds: [],
        error: error.code,
      };
    }

    throw new UnknownError({
      domain: DOMAIN,
      message: response.message || DEFAULT_ERROR_MESSAGE,
      details: {
        errors: response.errors ? JSON.stringify(response.errors) : undefined,
      },
    });
  } catch (error) {
    telemetry.increment(ETelemetryEvents.ChatsSynchronizeFail);

    let errorToLog = error;

    if (!GenericError.isLike(error)) {
      errorToLog = new UnknownError({
        domain: DOMAIN,
        message: DEFAULT_ERROR_MESSAGE,
        details: error,
      });
    }

    logger.error(errorToLog, {
      microserviceName: 'chats',
      pathApi: '/v2/web/chats/sync/',
      parameters: JSON.stringify(options),
    });

    return {
      chats: [],
      movedChatIds: [],
      error: 'unknownError',
    };
  }
}
